import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'

/** Группа данных, к которой приложение получит доступ. */
export interface McpAccessGroup {
  key: string
  label: string
}

/** Что показать на экране согласия. */
export interface McpConsentRequest {
  client: {
    id: string
    /**
     * Имя приходит из динамической регистрации клиента — его выбирает само
     * приложение. Показываем как есть, без доверия.
     */
    name: string
    /** Хост адреса возврата: по нему пользователь опознаёт приложение. */
    redirect_host: string | null
  }
  groups: McpAccessGroup[]
  already_granted: boolean
  expires_in_days: number
}

export interface McpConnection {
  id: string
  client_name: string | null
  connected_at: string | null
  last_used_at: string | null
  expires_at: string | null
}

/**
 * Исходные параметры запроса авторизации.
 *
 * Возвращаются серверу без изменений на каждом шаге: запрос авторизации не
 * хранится в сессии, сервер проверяет его заново.
 */
export interface McpAuthorizeParams {
  client_id: string
  redirect_uri?: string
  response_type: string
  scope?: string
  state?: string
  code_challenge?: string
  code_challenge_method?: string
}

/** Параметры OAuth, которые сервер ждёт обратно от экрана согласия. */
export const MCP_AUTHORIZE_PARAM_KEYS = [
  'client_id',
  'redirect_uri',
  'response_type',
  'scope',
  'state',
  'code_challenge',
  'code_challenge_method',
] as const

/** Собрать параметры авторизации из query-строки, отбросив пустые. */
export function readAuthorizeParams(search: URLSearchParams): McpAuthorizeParams | null {
  const params: Record<string, string> = {}

  MCP_AUTHORIZE_PARAM_KEYS.forEach((key) => {
    const value = search.get(key)
    if (value !== null && value !== '') {
      params[key] = value
    }
  })

  if (!params.client_id || !params.response_type) {
    return null
  }

  return params as unknown as McpAuthorizeParams
}

export const mcpConnectionsApi = {
  /** Данные для экрана согласия. */
  authorizeRequest: async (params: McpAuthorizeParams): Promise<McpConsentRequest> => {
    const response = await apiClient.get<{ data: McpConsentRequest }>(
      API_ENDPOINTS.OAUTH.AUTHORIZE_REQUEST,
      { params },
    )
    return response.data.data
  },

  /** Разрешить доступ; возвращает адрес возврата клиента. */
  approve: async (params: McpAuthorizeParams): Promise<string> => {
    const response = await apiClient.post<{ data: { redirect_url: string } }>(
      API_ENDPOINTS.OAUTH.AUTHORIZE_APPROVE,
      params,
    )
    return response.data.data.redirect_url
  },

  /** Отклонить доступ; возвращает адрес возврата клиента с ошибкой. */
  deny: async (params: McpAuthorizeParams): Promise<string> => {
    const response = await apiClient.post<{ data: { redirect_url: string } }>(
      API_ENDPOINTS.OAUTH.AUTHORIZE_DENY,
      params,
    )
    return response.data.data.redirect_url
  },

  getList: async (): Promise<McpConnection[]> => {
    const response = await apiClient.get<{ data: McpConnection[] }>(API_ENDPOINTS.OAUTH.CONNECTIONS)
    return response.data.data
  },

  revoke: async (id: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.OAUTH.CONNECTION(id))
  },
}
